import { ApiStreamEventType } from "./streamProtocol";
import type { ApiStreamEvent } from "../api/run";

/**
 * When a run's event stream is lost, its owner goes back to the server's state of the run
 * instead of ending the turn: a lost stream is not a lost run, and only the server says when a
 * run has ended. Shared by the web stream owner (`hooks/runStreamSession.ts`) and the
 * transport-neutral conversation runtime the iOS engine runs (`conversationRuntime.ts`).
 */

/** A stream that lived this long, or delivered anything, was healthy when it closed. */
export const HEALTHY_STREAM_MS = 15_000;
/**
 * The iOS runtime's budget of consecutive quick failures before its turn shows the connection
 * error. It cannot yet read a run's state back from the server the way the web owner does
 * (`hooks/runStreamSession.ts`), so without a budget a gone run would spin there for good.
 */
export const MAX_QUICK_REOPENS = 5;
/** Backoff before reopening after a quick failure: 1 s, 2 s, 4 s … up to this. */
const MAX_REOPEN_DELAY_MS = 15_000;
/** Reopen after a healthy stream closed (e.g. a proxy's idle timeout during a wait). */
const HEALTHY_REOPEN_DELAY_MS = 500;

/** The pause before the next reopen, given whether this failure was quick. */
export function reopenDelayMs(quick: boolean, quickFailures: number): number {
    return quick
        ? Math.min(1000 * 2 ** (quickFailures - 1), MAX_REOPEN_DELAY_MS)
        : HEALTHY_REOPEN_DELAY_MS;
}

/** True for the client-synthesized events of a lost connection (see api/run.ts). */
export function isConnectionLostEvent(event: ApiStreamEvent): boolean {
    return (
        event.seq === 0 &&
        (event.type === ApiStreamEventType.Error || event.type === ApiStreamEventType.Done) &&
        (event.data as { connectionLost?: boolean }).connectionLost === true
    );
}

/**
 * True when a lost stream was refused for what it is (a 4xx other than a timeout or a rate
 * limit: signed out, no access, no such run, a client the project no longer serves): opening it
 * again unchanged gets the same answer, so the owner reads the run's state another way.
 */
export function isStreamRefused(event: ApiStreamEvent): boolean {
    const status = (event.data as { httpStatus?: number }).httpStatus;
    return (
        status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429
    );
}
